import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { listUnits, createUnit, updateUnit, deleteUnit, getUnit, assignUsersToUnit, assignServicesToUnit, getUsers, getServices } from '../../services/api';
import { useConfirm } from '../../components/ConfirmDialog';
import PhoneInput from '../../components/PhoneInput';
import { formatPhoneBR } from '../../utils/phone';

const emptyForm = { name: '', address: '', phone: '', email: '', is_headquarters: false };

export default function TenantUnits() {
  const confirm = useConfirm();

  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | unitObject
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Assign modal
  const [assignModal, setAssignModal] = useState(null); // null | { unitId, type: 'users'|'services' }
  const [assignOptions, setAssignOptions] = useState([]);
  const [assignSelected, setAssignSelected] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);

  // Detail
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listUnits()
      .then(r => setUnits(r.data.units || []))
      .catch(() => toast.error('Erro ao carregar unidades'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setModal('new'); };
  const openEdit = u => {
    setForm({
      name: u.name,
      address: u.address || '',
      phone: u.phone || '',
      email: u.email || '',
      is_headquarters: !!u.is_headquarters,
    });
    setModal(u);
  };
  const closeModal = () => { if (!saving) setModal(null); };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      return toast.error('Nome da unidade obrigatório (mín. 2 caracteres)');
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        address: form.address.trim() || null,
        phone: form.phone || null,
        email: form.email.trim() || null,
        is_headquarters: form.is_headquarters,
      };
      if (modal === 'new') {
        await createUnit(payload);
        toast.success('Unidade criada!');
      } else {
        await updateUnit(modal.id, payload);
        toast.success('Unidade atualizada!');
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async u => {
    const ok = await confirm({
      title: `Excluir "${u.name}"?`,
      message: 'Profissionais e serviços vinculados serão desvinculados (não excluídos). Agendamentos históricos ficam sem unidade.',
      confirmText: 'Excluir',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteUnit(u.id);
      toast.success('Unidade removida');
      load();
      if (detail?.unit?.id === u.id) setDetail(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  const handleViewDetail = async u => {
    try {
      const r = await getUnit(u.id);
      setDetail(r.data);
    } catch {
      toast.error('Erro ao carregar detalhes');
    }
  };

  // Assign flow
  const openAssign = async (unitId, type) => {
    setAssignModal({ unitId, type });
    setAssignSelected([]);
    try {
      if (type === 'users') {
        const r = await getUsers();
        setAssignOptions((r.data.users || []).filter(u => u.active));
      } else {
        const r = await getServices();
        setAssignOptions(r.data.services || []);
      }
    } catch {
      toast.error('Erro ao carregar opções');
      setAssignModal(null);
    }
  };

  const toggleAssign = id => {
    setAssignSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    if (!assignSelected.length) return toast.error('Selecione pelo menos 1 item');
    setAssignSaving(true);
    try {
      if (assignModal.type === 'users') {
        await assignUsersToUnit(assignModal.unitId, { user_ids: assignSelected });
      } else {
        await assignServicesToUnit(assignModal.unitId, { service_ids: assignSelected });
      }
      toast.success(`${assignSelected.length} ${assignModal.type === 'users' ? 'profissionais' : 'serviços'} vinculados!`);
      setAssignModal(null);
      load();
      // Refresh detail if open
      if (detail?.unit?.id === assignModal.unitId) {
        const r = await getUnit(assignModal.unitId);
        setDetail(r.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao vincular');
    } finally {
      setAssignSaving(false);
    }
  };

  if (loading) return <div className="page-body" style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><div className="spinner" /></div>;

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Unidades / Filiais</h2>
          <p>Gerencie as unidades físicas do seu negócio</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Nova Unidade</button>
      </div>

      {units.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gray-400)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏢</div>
          <p>Nenhuma unidade cadastrada.</p>
          <p style={{ fontSize: 13 }}>Crie sua primeira unidade (matriz) para começar a organizar filiais.</p>
        </div>
      ) : (
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {units.map(u => (
            <div key={u.id} className="stat-card" style={{ padding: 20, cursor: 'pointer', position: 'relative' }} onClick={() => handleViewDetail(u)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--gray-900)' }}>
                    {u.is_headquarters ? '🏠 ' : '🏢 '}{u.name}
                  </div>
                  {u.address && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>{u.address}</div>}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {u.is_headquarters && <span className="badge badge-success" style={{ fontSize: 10 }}>Matriz</span>}
                  {u.active ? <span className="badge badge-info" style={{ fontSize: 10 }}>Ativa</span> : <span className="badge badge-gray" style={{ fontSize: 10 }}>Inativa</span>}
                </div>
              </div>

              {u.phone && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>📞 {formatPhoneBR(u.phone)}</div>}

              <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: 'var(--gray-400)' }}>
                <span>👥 {u.user_count || 0} profissionais</span>
                <span>🛎️ {u.service_count || 0} serviços</span>
                <span>📅 {u.appointments_month || 0} agend./mês</span>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }} onClick={e => e.stopPropagation()}>
                <button className="btn btn-xs btn-secondary" onClick={() => openEdit(u)}>Editar</button>
                <button className="btn btn-xs btn-secondary" onClick={() => openAssign(u.id, 'users')}>+ Equipe</button>
                <button className="btn btn-xs btn-secondary" onClick={() => openAssign(u.id, 'services')}>+ Serviços</button>
                <button className="btn btn-xs" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(u)}>Excluir</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail panel */}
      {detail && (
        <div style={{ marginTop: 24, background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--gray-200)', padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>{detail.unit?.is_headquarters ? '🏠' : '🏢'} {detail.unit?.name}</h3>
            <button className="btn btn-xs btn-secondary" onClick={() => setDetail(null)}>✕ Fechar</button>
          </div>

          <div className="grid-2" style={{ gap: 20 }}>
            <div>
              <h4 style={{ fontSize: 13, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Profissionais vinculados</h4>
              {detail.users?.length ? (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {detail.users.map(u => (
                    <li key={u.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--gray-100)', fontSize: 13 }}>
                      {u.active ? '🟢' : '🔴'} {u.name} <span style={{ color: 'var(--gray-400)' }}>({u.email})</span>
                    </li>
                  ))}
                </ul>
              ) : <p style={{ color: 'var(--gray-400)', fontSize: 13 }}>Nenhum profissional vinculado</p>}
            </div>
            <div>
              <h4 style={{ fontSize: 13, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Serviços vinculados</h4>
              {detail.services?.length ? (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {detail.services.map(s => (
                    <li key={s.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--gray-100)', fontSize: 13 }}>
                      {s.active ? '🟢' : '🔴'} {s.name} <span style={{ color: 'var(--gray-400)' }}>R$ {parseFloat(s.price || 0).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p style={{ color: 'var(--gray-400)', fontSize: 13 }}>Nenhum serviço vinculado</p>}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit modal */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modal === 'new' ? 'Nova Unidade' : `Editar ${modal.name}`}</h3>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="label">Nome *</label>
                  <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus />
                </div>
                <div className="form-group">
                  <label className="label">Endereço</label>
                  <input className="input" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Rua, número, bairro, cidade" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="label">Telefone</label>
                    <PhoneInput value={form.phone} onChange={(masked, normalized) => setForm(f => ({ ...f, phone: normalized || masked }))} />
                  </div>
                  <div className="form-group">
                    <label className="label">Email</label>
                    <input className="input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" id="hq" checked={form.is_headquarters} onChange={e => setForm(f => ({ ...f, is_headquarters: e.target.checked }))} />
                  <label htmlFor="hq" style={{ cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Esta é a unidade matriz (sede)</label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>
                  {saving ? '' : modal === 'new' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign modal */}
      {assignModal && (
        <div className="modal-overlay" onClick={() => !assignSaving && setAssignModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Vincular {assignModal.type === 'users' ? 'Profissionais' : 'Serviços'}</h3>
              <button className="modal-close" onClick={() => setAssignModal(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: 400, overflowY: 'auto' }}>
              {assignOptions.length === 0 ? (
                <p style={{ color: 'var(--gray-400)', textAlign: 'center' }}>Nenhum {assignModal.type === 'users' ? 'profissional' : 'serviço'} disponível</p>
              ) : (
                assignOptions.map(item => (
                  <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={assignSelected.includes(item.id)} onChange={() => toggleAssign(item.id)} />
                    <span style={{ fontSize: 13 }}>{item.name}</span>
                    {item.email && <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{item.email}</span>}
                  </label>
                ))
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setAssignModal(null)}>Cancelar</button>
              <button className={`btn btn-primary${assignSaving ? ' btn-loading' : ''}`} disabled={assignSaving || !assignSelected.length} onClick={handleAssign}>
                {assignSaving ? '' : `Vincular ${assignSelected.length} selecionados`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
